'use client';

import type React from 'react';
import { createContext, useContext } from 'react';

/**
 * Map of block type name (e.g. 'Link') to the component that should be rendered
 * instead of the block's default. Used by apps (e.g. matrix) to inject NextLink
 * for the Link block. When no provider wraps the tree (e.g. delta matrix omits
 * BlockComponentOverridesProvider), blocks use their default component choice.
 * Loosely typed so apps can pass components with specific props (e.g. NextLink).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- blocks pass their props; override components have varying prop shapes
export type BlockComponentOverridesMap = Record<string, React.ComponentType<any>>;

const BlockComponentOverridesContext = createContext<BlockComponentOverridesMap | null>(null);

export interface BlockComponentOverridesProviderProps {
  overrides: BlockComponentOverridesMap;
  children: React.ReactNode;
}

export function BlockComponentOverridesProvider({
  overrides,
  children,
}: BlockComponentOverridesProviderProps) {
  return (
    <BlockComponentOverridesContext.Provider value={overrides}>
      {children}
    </BlockComponentOverridesContext.Provider>
  );
}

/**
 * Returns the override component for the given block type, or undefined if no
 * provider is present or no override is registered. Safe when overrides are
 * empty (e.g. delta matrix does not mount BlockComponentOverridesProvider).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- override components have varying prop shapes; block passes known props
export function useBlockComponentOverride(blockType: string): React.ComponentType<any> | undefined {
  const overrides = useContext(BlockComponentOverridesContext);
  return overrides?.[blockType];
}
